import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import {
  CellFieldRowProvider,
  ControlledCellDateOnlyPicker,
  ControlledCellNumberField,
  ControlledCellSelect,
  ControlledCellTextField,
  ControlledSwitch,
  Icon,
  Placeholder,
  TooltipIconButton,
  useIfUser,
} from '@if/mui'
import { TableCell, TableRow } from '@mui/material'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { EPaymentMethod } from '@src/types/constants'
import { RotateCcw, Trash2 } from 'lucide-react'
import { memo, useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { JSX } from 'react/jsx-runtime'
import EditPaymentRowMark, { MARK_COLUMN_SX } from './EditPaymentRowMark'
import { EditPaymentBaseline, getEditPaymentRowProvenance } from './EditPayments-provenance'
import { editPaymentFieldIsDisabled, EditPaymentSchemaType, EditPaymentsSchemaType } from './EditPayments-schema'

interface EditPaymentTableRowProps {
  index: number
  onCriticalChange: (index: number, sortPayments?: boolean) => void
  onUncriticalChange: (index: number, sortPayments?: boolean) => void
  paymentMethodOptions: JSX.Element[]
  onDelete: (index: number, payment: EditPaymentSchemaType) => void
  onRestore: (index: number, payment: EditPaymentSchemaType) => void
  baseline: EditPaymentBaseline
  disabled?: boolean
  loanActivationDate: DateOnly
}

const EditPaymentTableRow = ({
  index,
  onCriticalChange,
  onUncriticalChange,
  paymentMethodOptions,
  onDelete,
  onRestore,
  baseline,
  disabled = false,
  loanActivationDate,
}: EditPaymentTableRowProps) => {
  const { control, setValue, trigger } = useFormContext<EditPaymentsSchemaType>()
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments

  // Optimization: useWatch to subscribe only to the specific row data
  // This prevents the entire table from re-rendering on every change
  const payment = useWatch({
    name: `payments.${index}`,
    control,
  })

  const handleCriticalChangeWithSorting = useCallback(() => {
    onCriticalChange(index, true)
  }, [index, onCriticalChange])

  const handleCriticalChange = useCallback(() => {
    onCriticalChange(index)
  }, [index, onCriticalChange])

  const handleUncriticalChange = useCallback(() => {
    onUncriticalChange(index)
  }, [index, onUncriticalChange])

  const resetSendSmsReminderOnPaymentMethodChanged = useCallback(
    (value: EPaymentMethod | null) => {
      if (!value) {
        return
      }
      setValue(`payments.${index}.sendSmsReminder`, false, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      void trigger(`payments.${index}.effectiveDate`)
      handleUncriticalChange()
    },
    [index, handleUncriticalChange, setValue, trigger],
  )

  const handleDeleteOrRestore = useCallback(() => {
    if (payment.isDeleted) onRestore(index, payment)
    else onDelete(index, payment)
  }, [index, onDelete, onRestore, payment])

  const fieldDisabled = useMemo<{
    [K in keyof Pick<
      EditPaymentSchemaType,
      'effectiveDate' | 'amount' | 'paymentMethod' | 'note' | 'sendSmsReminder'
    >]: boolean
  }>(
    () => ({
      effectiveDate: disabled || editPaymentFieldIsDisabled(payment, 'effectiveDate'),
      amount: disabled || editPaymentFieldIsDisabled(payment, 'amount'),
      paymentMethod: disabled || editPaymentFieldIsDisabled(payment, 'paymentMethod'),
      note: disabled || editPaymentFieldIsDisabled(payment, 'note'),
      sendSmsReminder: disabled || editPaymentFieldIsDisabled(payment, 'sendSmsReminder'),
    }),
    [payment, disabled],
  )

  const minStartDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance().getMinPaymentDate(
        payment.paymentMethod,
        loanActivationDate,
        canBypassAcpLock,
      ),
    [payment.paymentMethod, canBypassAcpLock, loanActivationDate],
  )

  const provenance = useMemo(() => getEditPaymentRowProvenance(payment, baseline), [payment, baseline])

  return (
    <CellFieldRowProvider deleted={payment.isDeleted}>
      <TableRow sx={{ bgcolor: payment.isDeleted ? 'background.sunken' : undefined }}>
        <TableCell align="center" padding="none" sx={MARK_COLUMN_SX}>
          <EditPaymentRowMark payment={payment} provenance={provenance} />
        </TableCell>

        <TableCell align="center" sx={{ width: '1%', color: payment.isDeleted ? 'text.disabled' : undefined }}>
          {payment.paymentNumber ?? <Placeholder>&mdash;</Placeholder>}
        </TableCell>

        <TableCell sx={{ width: '1%', minWidth: 150 }}>
          <ControlledCellDateOnlyPicker
            name={`payments.${index}.effectiveDate`}
            control={control}
            label={t('common.date')}
            onChange={handleCriticalChangeWithSorting}
            disabled={fieldDisabled.effectiveDate}
            minDate={minStartDate}
          />
        </TableCell>

        <TableCell sx={{ width: '1%', minWidth: 120 }}>
          <ControlledCellNumberField
            name={`payments.${index}.amount`}
            control={control}
            label={t('loan.payment.amount')}
            isMoney
            onChange={handleCriticalChange}
            disabled={fieldDisabled.amount}
          />
        </TableCell>

        <TableCell sx={{ width: '1%', minWidth: 170 }}>
          <ControlledCellSelect
            name={`payments.${index}.paymentMethod`}
            control={control}
            label={t('loan.payment.pmtModeTitle')}
            options={paymentMethodOptions}
            disableEmptyOption
            onChange={resetSendSmsReminderOnPaymentMethodChanged}
            disabled={fieldDisabled.paymentMethod}
          />
        </TableCell>

        <TableCell>
          <ControlledCellTextField
            name={`payments.${index}.note`}
            control={control}
            label={t('loan.payment.options.addNote')}
            onChange={handleUncriticalChange}
            disabled={fieldDisabled.note}
          />
        </TableCell>

        <TableCell align="center" sx={{ width: '1%' }}>
          <ControlledSwitch
            name={`payments.${index}.sendSmsReminder`}
            control={control}
            label=""
            onChange={handleUncriticalChange}
            disabled={fieldDisabled.sendSmsReminder}
            controlProps={{ inputProps: { 'aria-label': t('loan.payment.editPayments.smsReminder') } }}
            sx={{ m: 0 }}
          />
        </TableCell>

        <TableCell align="center" sx={{ width: '1%' }}>
          <TooltipIconButton
            title={payment.isDeleted ? t('loan.payment.restore') : t('loan.payment.delete')}
            onClick={handleDeleteOrRestore}
            color={payment.isDeleted ? 'inherit' : 'error'}
            disabled={disabled || payment.isLocked}
          >
            <Icon icon={payment.isDeleted ? RotateCcw : Trash2} size="sm" />
          </TooltipIconButton>
        </TableCell>
      </TableRow>
    </CellFieldRowProvider>
  )
}

export default memo(EditPaymentTableRow)
